import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, api, type ClaudeKey } from "@/lib/api";
import ConfirmDialog from "@/ui/ConfirmDialog";
import { useToast } from "@/ui/Toast";

const ORIGEN: Record<string, { etiqueta: string; clase: string }> = {
  base: {
    etiqueta: "guardada aquí",
    clase: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  },
  archivo: {
    etiqueta: "la del archivo del servidor",
    clase: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  },
  ninguno: {
    etiqueta: "sin configurar",
    clase: "bg-zinc-500/10 text-zinc-400 border-zinc-500/30",
  },
};

/**
 * La llave de Claude, rotable desde aquí.
 *
 * Antes vivía sólo en el archivo `.env` del servidor: rotarla exigía entrar por
 * terminal, editar el archivo con permisos de root y reiniciar dos servicios.
 */
export default function LlaveClaude() {
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const [estado, setEstado] = useState<ClaudeKey | null>(null);
  const [cargando, setCargando] = useState(true);
  const [llave, setLlave] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [probando, setProbando] = useState(false);
  const [confirmarQuitar, setConfirmarQuitar] = useState(false);

  const cargar = useCallback(async () => {
    try {
      setEstado(await api.get<ClaudeKey>("/api/admin/claude-key"));
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toastRef.current.error("No pude leer el estado de la llave", msg);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  async function guardar() {
    const nueva = llave.trim();
    if (!nueva || guardando) return;
    setGuardando(true);
    try {
      const r = await api.put<ClaudeKey>("/api/admin/claude-key", { llave: nueva });
      setEstado(r);
      setLlave("");
      toast.success(
        "Llave guardada",
        r.modelo_alcanzable ? `Probada con ${r.modelo_alcanzable}.` : undefined,
      );
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No guardé la llave", msg);
    } finally {
      setGuardando(false);
    }
  }

  async function probar() {
    setProbando(true);
    try {
      const r = await api.post<ClaudeKey>("/api/admin/claude-key/probar");
      setEstado(r);
      toast.success(
        "La llave funciona",
        r.modelo_alcanzable ? `Respondió ${r.modelo_alcanzable}.` : undefined,
      );
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("La llave no funciona", msg);
    } finally {
      setProbando(false);
    }
  }

  async function quitar() {
    try {
      const r = await api.del<ClaudeKey>("/api/admin/claude-key");
      setEstado(r);
      toast.success(
        "Llave quitada",
        r.configurada ? "Se volvió a la del archivo del servidor." : "La IA queda sin llave.",
      );
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude quitarla", msg);
    } finally {
      setConfirmarQuitar(false);
    }
  }

  if (cargando || !estado) {
    return <p className="text-sm text-zinc-500">Cargando…</p>;
  }

  const origen = ORIGEN[estado.origen] ?? ORIGEN.ninguno;

  return (
    <div className="card max-w-2xl space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-wide">
        <span className={`rounded border px-2 py-0.5 ${origen.clase}`}>
          {estado.configurada ? origen.etiqueta : "sin configurar"}
        </span>
        {estado.pista && (
          <span className="font-mono normal-case text-zinc-500">{estado.pista}</span>
        )}
      </div>

      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          type="password"
          value={llave}
          onChange={(e) => setLlave(e.target.value)}
          placeholder="Llave nueva (la da la consola de Anthropic)"
          autoComplete="off"
          className="input flex-1 font-mono"
        />
        <button
          type="button"
          onClick={() => void guardar()}
          disabled={guardando || !llave.trim()}
          className="btn-primary w-full text-sm disabled:opacity-50 sm:w-auto"
        >
          {guardando ? "Probando y guardando…" : "Guardar"}
        </button>
      </div>

      <p className="text-[11px] text-zinc-500">
        Antes de guardarla se comprueba con Anthropic; si no funciona, no se guarda y la de ahora se
        queda como está. Se guarda <strong>cifrada</strong> y nunca se vuelve a mostrar. Aplica al
        momento: no hay que reiniciar nada.
      </p>

      <div className="flex flex-wrap items-center gap-4 border-t border-ink-700 pt-3">
        <button
          type="button"
          onClick={() => void probar()}
          disabled={probando || !estado.configurada}
          className="btn-ghost text-sm disabled:opacity-50"
        >
          {probando ? "Probando…" : "Probar"}
        </button>
        {estado.origen === "base" && (
          <button
            type="button"
            onClick={() => setConfirmarQuitar(true)}
            className="text-sm text-red-400 hover:text-red-300"
          >
            Quitar la llave guardada
          </button>
        )}
      </div>

      <ConfirmDialog
        open={confirmarQuitar}
        onOpenChange={setConfirmarQuitar}
        title="¿Quitar la llave guardada?"
        description={
          estado.hay_respaldo_en_archivo
            ? "Se volverá a usar la llave del archivo del servidor, así que la IA sigue funcionando."
            : "No hay otra llave de respaldo: la IA dejará de funcionar hasta que guardes una nueva."
        }
        confirmLabel="Quitarla"
        variant="danger"
        onConfirm={() => void quitar()}
      />
    </div>
  );
}
